import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Clock, CheckCircle2, FileArchive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, api, downloadFile, formatErr, previewUrl } from "@/lib/api";
import { PageHeader } from "@/components/Common";

export default function PurchasesPage() {
  const [photos, setPhotos] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/me/purchases").then((r) => setPhotos(r.data)); }, []);

  const download = async (p) => {
    setBusy(p.id);
    try {
      await downloadFile(`/photos/${p.id}/download`, p.filename || `${p.id}.jpg`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(null);
    }
  };

  const [zipping, setZipping] = useState(false);
  const waiting = photos ? photos.filter((p) => !p.hd_ready).length : 0;
  const downloadZip = async () => {
    setZipping(true);
    try {
      const { data } = await api.post("/me/purchases/zip-token");
      toast.success(`Menyiapkan ZIP berisi ${data.count} foto HD, unduhan akan dimulai sebentar lagi`);
      window.location.href = `${API}/me/purchases/zip?token=${encodeURIComponent(data.token)}`;
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setTimeout(() => setZipping(false), 3000);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <PageHeader label="Foto tanpa watermark" title="Koleksi Saya">
        {photos?.length > 0 && (
          <div className="flex flex-col items-start sm:items-end gap-1">
            <Button onClick={downloadZip} disabled={waiting > 0 || zipping} className="rounded-xl h-11 bg-blue-600 hover:bg-blue-700" data-testid="purchases-zip-button">
              <FileArchive className="mr-2 h-4 w-4" />{zipping ? "Menyiapkan ZIP..." : `Unduh semua HD (ZIP · ${photos.length} foto)`}
            </Button>
            {waiting > 0 && <p className="text-xs text-amber-700" data-testid="purchases-zip-waiting">Aktif setelah semua HD terkirim · {waiting} foto masih menunggu file HD</p>}
          </div>
        )}
      </PageHeader>
      {photos === null ? <p className="text-zinc-500">Memuat...</p> : photos.length === 0 ? (
        <p className="text-zinc-500" data-testid="purchases-empty">Belum ada foto yang lunas.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="purchases-grid">
          {photos.map((p) => (
            <div key={p.id} className="rounded-2xl overflow-hidden bg-white border border-zinc-200" data-testid={`purchase-card-${p.id}`}>
              <img src={previewUrl(p.id)} alt="" className="w-full aspect-[4/3] object-cover" />
              <div className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{p.event_title}</p>
                  <p className="text-xs text-zinc-500">{p.photographer_name}</p>
                  {p.hd_ready
                    ? <p className="text-xs font-semibold text-emerald-700 mt-1" data-testid={`purchase-hd-ready-${p.id}`}><CheckCircle2 className="inline h-3 w-3 mr-1" />File HD siap</p>
                    : <p className="text-xs font-semibold text-amber-700 mt-1" data-testid={`purchase-hd-pending-${p.id}`}><Clock className="inline h-3 w-3 mr-1" />Menunggu file HD dari fotografer</p>}
                </div>
                <Button size="sm" onClick={() => download(p)} disabled={busy === p.id} className="rounded-xl bg-zinc-900 hover:bg-zinc-800" data-testid={`purchase-download-${p.id}`}>
                  <Download className="mr-1.5 h-4 w-4" />{busy === p.id ? "..." : p.hd_ready ? "Unduh HD" : "Unduh low-res"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
